import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { contentAPI, paymentsAPI } from '../services/api';
import { Play, Star, Clock, Calendar, Lock, ChevronLeft, DollarSign, CreditCard, Loader2, PlayCircle, X, ChevronDown, ChevronUp } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import FavoriteButton from '../components/FavoriteButton';
import CommentsSection from '../components/CommentsSection';
import VideoPlayerWithAds from '../components/VideoPlayerWithAds';
import YouTubePlayer from '../components/YouTubePlayer';
import VideoReviewBubbles from '../components/VideoReviewBubbles';
import MuxPlayer from '@mux/mux-player-react';
import api from '../services/api';

const getFullCountdown = (releaseDateStr) => {
  if (!releaseDateStr) return null;
  const now = new Date();
  const release = new Date(releaseDateStr);
  const diff = release - now;
  if (diff <= 0) return null;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const secs = Math.floor((diff % (1000 * 60)) / 1000);
  return { days, hours, mins, secs, total: diff };
};

const CountdownBadge = ({ releaseDate }) => {
  const [countdown, setCountdown] = React.useState(() => getFullCountdown(releaseDate));
  React.useEffect(() => {
    const interval = setInterval(() => setCountdown(getFullCountdown(releaseDate)), 1000);
    return () => clearInterval(interval);
  }, [releaseDate]);
  if (!countdown) return null;
  const relDate = new Date(releaseDate);
  const dateStr = relDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'America/Los_Angeles' });
  return (
    <div className="space-y-2">
      <div className="inline-flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 rounded-lg shadow-lg">
        <Lock className="h-5 w-5 text-white" />
        <span className="text-white font-bold text-lg">Releases {dateStr}</span>
      </div>
      <div className="flex items-center space-x-3">
        {[{ v: countdown.days, l: 'Days' }, { v: countdown.hours, l: 'Hrs' }, { v: countdown.mins, l: 'Min' }, { v: countdown.secs, l: 'Sec' }].map(({ v, l }) => (
          <div key={l} className="flex flex-col items-center px-3 py-2 bg-white/10 rounded-lg backdrop-blur border border-white/10 min-w-[50px]">
            <span className="text-xl font-bold text-white">{String(v).padStart(2, '0')}</span>
            <span className="text-xs text-white/60">{l}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const EpisodeCountdown = ({ releaseDate }) => {
  const [cd, setCd] = React.useState(() => getFullCountdown(releaseDate));
  React.useEffect(() => {
    const interval = setInterval(() => setCd(getFullCountdown(releaseDate)), 60000);
    return () => clearInterval(interval);
  }, [releaseDate]);
  if (!cd) return null;
  const text = cd.days > 0 ? `${cd.days}d ${cd.hours}h` : `${cd.hours}h ${cd.mins}m`;
  return <span className="text-xs font-medium text-white/90 bg-black/50 px-2 py-0.5 rounded-full">{text}</span>;
};

const ContentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ppvAccess, setPpvAccess] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [isWatching, setIsWatching] = useState(false);
  const [watchingTrailer, setWatchingTrailer] = useState(false);
  const heroVideoRef = useRef(null);
  
  // Episode states for TV shows
  const [episodes, setEpisodes] = useState([]);
  const [loadingEpisodes, setLoadingEpisodes] = useState(false);
  const [selectedEpisode, setSelectedEpisode] = useState(null);
  const [watchingEpisode, setWatchingEpisode] = useState(null);
  const [expandedSeasons, setExpandedSeasons] = useState(new Set([1]));

  const loadContent = useCallback(async () => {
    try {
      const response = await contentAPI.getById(id);
      setContent(response.data);
      
      // Load episodes if it's a TV show
      if (response.data.content_type === 'tv_show') {
        loadEpisodes(id);
      }
    } catch (error) {
      console.error('Error loading content:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadEpisodes = async (contentId) => {
    setLoadingEpisodes(true);
    try {
      const response = await api.get(`/content/${contentId}/episodes`);
      setEpisodes(response.data || []);
    } catch (error) {
      console.error('Error loading episodes:', error);
      setEpisodes([]);
    } finally {
      setLoadingEpisodes(false);
    }
  };

  const checkPPVAccess = useCallback(async () => {
    try {
      const response = await paymentsAPI.checkPPVAccess(id);
      setPpvAccess(response.data);
    } catch (error) {
      setPpvAccess({ has_access: false, price: 4.99 });
    }
  }, [id]);

  useEffect(() => {
    loadContent();
  }, [loadContent]);

  useEffect(() => {
    if (content?.is_ppv && isAuthenticated) {
      checkPPVAccess();
    }
  }, [content?.is_ppv, isAuthenticated, checkPPVAccess]);

  const handleStripePayment = async () => {
    if (!isAuthenticated) {
      navigate('/signin');
      return;
    }
    setProcessingPayment(true);
    try {
      const response = await paymentsAPI.createPPVCheckout(id);
      window.location.href = response.data.checkout_url;
    } catch (error) {
      alert('Payment failed');
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleWatchNow = () => {
    setIsWatching(true);
    setWatchingTrailer(false);
  };

  const handleWatchTrailer = () => {
    setWatchingTrailer(true);
    setIsWatching(false);
  };

  const handleBackToHero = () => {
    setIsWatching(false);
    setWatchingTrailer(false);
    setWatchingEpisode(null);
  };

  const handleWatchEpisode = (episode) => {
    setWatchingEpisode(episode);
    setSelectedEpisode(null);
  };

  const toggleSeason = (season) => {
    setExpandedSeasons(prev => {
      const newSet = new Set(prev);
      if (newSet.has(season)) {
        newSet.delete(season);
      } else {
        newSet.add(season);
      }
      return newSet;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-12 w-12 text-electric animate-spin" />
      </div>
    );
  }

  if (!content) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <h2 className="text-2xl font-bold text-foreground">Content not found</h2>
      </div>
    );
  }

  const canWatch = !content.is_locked && (!content.is_premium && !content.is_ppv || user?.is_premium || ppvAccess?.has_access);
  const showPPVGate = content.is_ppv && !ppvAccess?.has_access && !user?.is_premium && !content.is_locked;
  const hasMuxVideo = content.mux_playback_id && content.mux_status === 'ready';
  const hasYouTube = !!content.youtube_id;
  const hasTrailer = content.trailer_playback_id && content.trailer_status === 'ready';
  const isPremiumUser = user?.is_premium || false;
  const isTVShow = content.content_type === 'tv_show';
  const isContentLocked = content.is_locked;

  // Group episodes by season
  const episodesBySeason = episodes.reduce((acc, episode) => {
    const season = episode.season || 1;
    if (!acc[season]) acc[season] = [];
    acc[season].push(episode);
    return acc;
  }, {});
  const seasons = Object.keys(episodesBySeason).map(Number).sort((a, b) => a - b);

  // Episode player mode
  if (watchingEpisode && (watchingEpisode.mux_playback_id || watchingEpisode.youtube_id)) {
    return (
      <div className="min-h-screen bg-black" data-testid="content-detail-page">
        <button 
          onClick={handleBackToHero} 
          className="fixed top-4 left-4 z-50 flex items-center space-x-2 px-4 py-2 rounded-xl bg-white/10 backdrop-blur-xl border border-white/20 hover:bg-white/20 transition-colors"
          data-testid="back-button"
        >
          <ChevronLeft className="h-5 w-5" /><span>Back</span>
        </button>
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-xl bg-white/10 backdrop-blur-xl border border-white/20">
          <span className="text-white text-sm">S{watchingEpisode.season}E{watchingEpisode.episode_number}: {watchingEpisode.title}</span>
        </div>
        <div className="w-full h-screen">
          {watchingEpisode.youtube_id ? (
            <YouTubePlayer
              videoId={watchingEpisode.youtube_id}
              poster={watchingEpisode.thumbnail || content.thumbnail}
              autoPlay
              className="w-full h-full"
              data-testid="episode-youtube-player"
            />
          ) : (
            <VideoPlayerWithAds 
              playbackId={watchingEpisode.mux_playback_id}
              contentId={content.id}
              poster={watchingEpisode.thumbnail || content.thumbnail}
              className="w-full h-full"
              autoPlay={true}
              isPremiumUser={isPremiumUser}
              onVideoStart={() => console.log('Episode started')}
              onVideoEnd={() => console.log('Episode ended')}
            />
          )}
        </div>
      </div>
    );
  }

  // YouTube embed mode (music videos, etc.)
  if (isWatching && canWatch && hasYouTube) {
    return (
      <div className="min-h-screen bg-black" data-testid="content-detail-page">
        <button
          onClick={handleBackToHero}
          className="fixed top-4 left-4 z-50 flex items-center space-x-2 px-4 py-2 rounded-xl bg-white/10 backdrop-blur-xl border border-white/20 hover:bg-white/20 transition-colors"
          data-testid="back-button"
        >
          <ChevronLeft className="h-5 w-5" /><span>Back</span>
        </button>
        <div className="w-full h-screen flex items-center justify-center">
          <YouTubePlayer
            videoId={content.youtube_id}
            poster={content.thumbnail}
            autoPlay
            className="w-full h-full"
            data-testid="content-youtube-player"
          />
        </div>
      </div>
    );
  }

  // Full screen video player mode with ads
  if (isWatching && canWatch && hasMuxVideo) {    return (
      <div className="min-h-screen bg-black" data-testid="content-detail-page">
        <button 
          onClick={handleBackToHero} 
          className="fixed top-4 left-4 z-50 flex items-center space-x-2 px-4 py-2 rounded-xl bg-white/10 backdrop-blur-xl border border-white/20 hover:bg-white/20 transition-colors"
          data-testid="back-button"
        >
          <ChevronLeft className="h-5 w-5" /><span>Back</span>
        </button>
        <div className="w-full h-screen">
          <VideoPlayerWithAds 
            playbackId={content.mux_playback_id}
            contentId={content.id}
            poster={content.thumbnail}
            className="w-full h-full"
            autoPlay={true}
            isPremiumUser={isPremiumUser}
            onVideoStart={() => console.log('Video started')}
            onVideoEnd={() => console.log('Video ended')}
          />
        </div>
      </div>
    );
  }

  // Trailer player mode
  if (watchingTrailer && hasTrailer) {
    return (
      <div className="min-h-screen bg-black" data-testid="content-detail-page">
        <button 
          onClick={handleBackToHero} 
          className="fixed top-4 left-4 z-50 flex items-center space-x-2 px-4 py-2 rounded-xl bg-white/10 backdrop-blur-xl border border-white/20 hover:bg-white/20 transition-colors"
          data-testid="back-button"
        >
          <ChevronLeft className="h-5 w-5" /><span>Back</span>
        </button>
        <div className="w-full h-screen">
          <MuxPlayer 
            playbackId={content.trailer_playback_id}
            poster={content.thumbnail}
            autoPlay={true}
            streamType="on-demand"
            style={{ width: '100%', height: '100%' }}
            primaryColor="#00F5D4"
            secondaryColor="#000000"
            accentColor="#00F5D4"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="content-detail-page">
      <button 
        onClick={() => navigate(-1)} 
        className="fixed top-4 left-4 md:left-[calc(20%+1rem)] z-50 flex items-center space-x-2 px-4 py-2 rounded-xl bg-white/10 backdrop-blur-xl border border-white/20 hover:bg-white/20 transition-colors" 
        data-testid="back-button"
      >
        <ChevronLeft className="h-5 w-5" /><span>Back</span>
      </button>

      {/* Hero Section with Auto-Playing Trailer */}
      <div className="relative w-full h-[70vh] bg-black overflow-hidden">
        {/* Background - Auto-play trailer if available, else YouTube video muted, else thumbnail */}
        {hasTrailer ? (
          <div className="w-full h-full" style={{ position: 'relative' }}>
            <MuxPlayer
              playbackId={content.trailer_playback_id}
              poster={content.thumbnail}
              autoPlay
              muted
              loop
              streamType="on-demand"
              style={{ 
                width: '100%', 
                height: '100%',
                '--controls': 'none',
                '--media-object-fit': 'cover',
                position: 'absolute',
                top: 0,
                left: 0
              }}
              primaryColor="transparent"
              secondaryColor="transparent"
            />
          </div>
        ) : hasYouTube ? (
          <div className="absolute inset-0 w-full h-full overflow-hidden pointer-events-none">
            {/* YouTube background: muted autoplay loop, scaled up to hide branding/controls */}
            <iframe
              title={`${content.title} preview`}
              src={`https://www.youtube.com/embed/${content.youtube_id}?autoplay=1&mute=1&controls=0&loop=1&playlist=${content.youtube_id}&playsinline=1&modestbranding=1&rel=0&showinfo=0&iv_load_policy=3&disablekb=1`}
              allow="autoplay; encrypted-media; picture-in-picture"
              frameBorder="0"
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
              style={{
                width: '177.78vh', // 16:9 width based on viewport height
                height: '56.25vw', // 16:9 height based on viewport width
                minWidth: '100%',
                minHeight: '100%',
                pointerEvents: 'none',
              }}
              data-testid="hero-youtube-bg"
            />
          </div>
        ) : (
          <img 
            src={content.thumbnail} 
            alt={content.title} 
            className="w-full h-full object-cover"
          />
        )}
        
        {/* Gradient Overlays */}
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/70 to-transparent"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent"></div>

        {/* Content Overlay */}
        <div className="absolute inset-0 flex items-end pb-12">
          <div className="max-w-7xl mx-auto px-4 md:px-8 w-full">
            <div className="flex items-end space-x-8">
              {/* Thumbnail */}
              <div className="hidden md:block flex-shrink-0">
                <img 
                  src={content.thumbnail} 
                  alt={content.title}
                  className="w-40 lg:w-48 rounded-xl shadow-2xl border-2 border-white/10"
                  style={{
                    aspectRatio: content.content_type === 'music_video' ? '16/9' : '9/16'
                  }}
                />
              </div>
              
              {/* Info */}
              <div className="flex-1 space-y-4">
                {/* Coming Soon / Release Date Badge */}
                {isContentLocked && content.release_date && (
                  <CountdownBadge releaseDate={content.release_date} />
                )}
                {content.coming_soon && !isContentLocked && !content.release_date && (
                  <div className="inline-flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 rounded-lg shadow-lg">
                    <Clock className="h-5 w-5 text-white" />
                    <span className="text-white font-bold text-lg">Coming Soon</span>
                  </div>
                )}

                {/* Title */}
                <h1 className="text-3xl md:text-5xl font-bold text-white drop-shadow-lg">
                  {content.title}
                </h1>

                {/* Meta Info */}
                <div className="flex flex-wrap items-center gap-4 text-white/80">
                  <span className="flex items-center space-x-2">
                    <Calendar className="h-4 w-4" />
                    <span>{content.year}</span>
                  </span>
                  {content.duration && (
                    <span className="flex items-center space-x-2">
                      <Clock className="h-4 w-4" />
                      <span>{content.duration}</span>
                    </span>
                  )}
                  <span className="flex items-center space-x-2">
                    <Star className="h-4 w-4 text-electric fill-electric" />
                    <span className="text-white font-semibold">{content.rating?.toFixed(1) || '0.0'}</span>
                  </span>
                  {content.is_ppv && (
                    <span className="px-3 py-1 rounded-full bg-amber-500/20 text-sm text-amber-400 font-medium">
                      ${content.ppv_price || 4.99} PPV
                    </span>
                  )}
                </div>

                {/* Genres */}
                <div className="flex flex-wrap gap-2">
                  {content.genre?.map((g, i) => (
                    <span key={i} className="px-3 py-1 rounded-full bg-white/10 backdrop-blur text-sm text-white/90">
                      {g}
                    </span>
                  ))}
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap items-center gap-4 pt-2">
                  {isContentLocked ? (
                    /* Locked - show countdown and trailer only */
                    <>
                      <div className="flex items-center space-x-3 px-6 py-3 rounded-xl bg-white/10 border border-white/20 text-white/70">
                        <Lock className="h-5 w-5" />
                        <span>Available Soon</span>
                      </div>
                      {hasTrailer && (
                        <button
                          onClick={() => setWatchingTrailer(true)}
                          className="flex items-center space-x-3 px-6 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold hover:from-amber-600 hover:to-orange-600 transition-all shadow-lg"
                          data-testid="watch-trailer-btn"
                        >
                          <PlayCircle className="h-5 w-5" />
                          <span>Watch Trailer</span>
                        </button>
                      )}
                    </>
                  ) : content.coming_soon ? (
                    /* Coming Soon - Only show trailer button */
                    hasTrailer && (
                      <button
                        onClick={() => setWatchingTrailer(true)}
                        className="flex items-center space-x-3 px-6 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold hover:from-amber-600 hover:to-orange-600 transition-all shadow-lg"
                        data-testid="watch-trailer-btn"
                      >
                        <PlayCircle className="h-5 w-5" />
                        <span>Watch Trailer</span>
                      </button>
                    )
                  ) : canWatch && (hasMuxVideo || hasYouTube) ? (
                    <button
                      onClick={handleWatchNow}
                      className="flex items-center space-x-3 px-6 py-3 rounded-xl bg-electric text-background font-semibold hover:bg-electric/90 transition-all transform hover:scale-105"
                      data-testid="watch-now-btn"
                    >
                      <Play className="h-5 w-5 fill-background" />
                      <span>Watch Now</span>
                    </button>
                  ) : showPPVGate ? (
                    <button
                      onClick={handleStripePayment}
                      disabled={processingPayment}
                      className="flex items-center space-x-3 px-6 py-3 rounded-xl bg-amber-500 text-background font-semibold hover:bg-amber-400 transition-all disabled:opacity-50"
                      data-testid="ppv-unlock-btn"
                    >
                      {processingPayment ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        <>
                          <DollarSign className="h-5 w-5" />
                          <span>Unlock ${content.ppv_price || 4.99}</span>
                        </>
                      )}
                    </button>
                  ) : content.is_premium && !user?.is_premium ? (
                    <button
                      onClick={() => navigate('/subscription')}
                      className="flex items-center space-x-3 px-6 py-3 rounded-xl bg-electric text-background font-semibold hover:bg-electric/90 transition-all"
                      data-testid="upgrade-btn"
                    >
                      <Lock className="h-5 w-5" />
                      <span>Upgrade</span>
                    </button>
                  ) : (
                    <div className="flex items-center space-x-3 px-6 py-3 rounded-xl bg-white/20 text-white/60">
                      <Play className="h-5 w-5" />
                      <span>Coming Soon</span>
                    </div>
                  )}

                  {/* Trailer Button */}
                  {hasTrailer && (
                    <button
                      onClick={handleWatchTrailer}
                      className="flex items-center space-x-3 px-6 py-3 rounded-xl bg-white/10 backdrop-blur border border-white/20 text-white font-semibold hover:bg-white/20 transition-all"
                      data-testid="trailer-btn"
                    >
                      <PlayCircle className="h-5 w-5" />
                      <span>Trailer</span>
                    </button>
                  )}

                  {/* Favorite Button */}
                  <FavoriteButton contentId={content.id} size="lg" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Details Section */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 space-y-8">
        {/* Synopsis */}
        <div>
          <h2 className="text-xl font-semibold text-foreground mb-3">Synopsis</h2>
          <p className="text-foreground/80 text-lg leading-relaxed">{content.description}</p>
        </div>

        {/* Cast & Director */}
        {(content.cast?.length > 0 || content.director) && (
          <div className="grid md:grid-cols-2 gap-6">
            {content.director && (
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-2">Director</h3>
                <p className="text-foreground">{content.director}</p>
              </div>
            )}
            {content.cast?.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-2">Cast</h3>
                <p className="text-foreground">{content.cast.join(', ')}</p>
              </div>
            )}
          </div>
        )}

        {/* Episodes Section for TV Shows */}
        {isTVShow && (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-foreground">Episodes</h2>
            
            {loadingEpisodes ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 text-electric animate-spin" />
              </div>
            ) : episodes.length === 0 ? (
              <div className="text-center py-12 bg-white/5 rounded-xl">
                <p className="text-muted-foreground">No episodes available yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {seasons.map(season => (
                  <div key={season} className="bg-white/5 rounded-xl overflow-hidden">
                    {/* Season Header */}
                    <button
                      onClick={() => toggleSeason(season)}
                      className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors"
                    >
                      <div className="flex items-center space-x-3">
                        <span className="text-lg font-semibold text-foreground">Season {season}</span>
                        <span className="px-2 py-0.5 bg-white/10 rounded-full text-xs text-muted-foreground">
                          {episodesBySeason[season].length} episodes
                        </span>
                      </div>
                      {expandedSeasons.has(season) ? (
                        <ChevronUp className="h-5 w-5 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-muted-foreground" />
                      )}
                    </button>

                    {/* Episodes List */}
                    {expandedSeasons.has(season) && (
                      <div className="border-t border-white/10">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 p-4">
                          {episodesBySeason[season]
                            .sort((a, b) => a.episode_number - b.episode_number)
                            .map((episode) => (
                              <div
                                key={episode.id}
                                onClick={() => !episode.is_locked && setSelectedEpisode(episode)}
                                className={`group cursor-pointer bg-white/5 rounded-xl overflow-hidden transition-all ${episode.is_locked ? 'opacity-80' : 'hover:bg-white/10 hover:ring-2 hover:ring-electric/50'}`}
                              >
                                {/* Episode Thumbnail */}
                                <div className="relative aspect-video bg-white/10">
                                  {episode.thumbnail ? (
                                    <img
                                      src={episode.thumbnail}
                                      alt={episode.title}
                                      className={`w-full h-full object-cover ${episode.is_locked ? 'blur-sm brightness-50' : ''}`}
                                    />
                                  ) : (
                                    <div className={`w-full h-full flex items-center justify-center ${episode.is_locked ? 'opacity-50' : ''}`}>
                                      <Play className="h-8 w-8 text-muted-foreground" />
                                    </div>
                                  )}
                                  {/* Lock overlay for locked episodes */}
                                  {episode.is_locked ? (
                                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/30">
                                      <div className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center border border-white/20 mb-1">
                                        <Lock className="h-5 w-5 text-white" />
                                      </div>
                                      {episode.release_date && (
                                        <EpisodeCountdown releaseDate={episode.release_date} />
                                      )}
                                    </div>
                                  ) : (
                                    <>
                                      {/* Play overlay */}
                                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                        <div className="w-12 h-12 rounded-full bg-electric flex items-center justify-center">
                                          <Play className="h-6 w-6 text-background fill-background" />
                                        </div>
                                      </div>
                                    </>
                                  )}
                                  {/* Episode number badge */}
                                  <div className="absolute top-2 left-2 px-2 py-1 bg-black/60 rounded text-xs text-white">
                                    E{episode.episode_number}
                                  </div>
                                  {/* Duration badge */}
                                  {episode.duration && (
                                    <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/60 rounded text-xs text-white">
                                      {episode.duration}
                                    </div>
                                  )}
                                </div>
                                {/* Episode Info */}
                                <div className="p-3">
                                  <h4 className="font-medium text-foreground truncate">{episode.title}</h4>
                                  <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                                    {episode.description || 'No description available'}
                                  </p>
                                </div>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Episode Detail Modal */}
        {selectedEpisode && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setSelectedEpisode(null)}>
            <div className="bg-background border border-white/10 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
              {/* Episode Thumbnail/Preview */}
              <div className="relative aspect-video bg-white/10">
                {selectedEpisode.thumbnail ? (
                  <img
                    src={selectedEpisode.thumbnail}
                    alt={selectedEpisode.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-electric/20 to-purple-500/20">
                    <Play className="h-16 w-16 text-muted-foreground" />
                  </div>
                )}
                {/* Close button */}
                <button
                  onClick={() => setSelectedEpisode(null)}
                  className="absolute top-4 right-4 p-2 bg-black/50 hover:bg-black/70 rounded-full transition-colors"
                >
                  <X className="h-5 w-5 text-white" />
                </button>
                {/* Play button overlay */}
                {(selectedEpisode.mux_playback_id || selectedEpisode.youtube_id) && (
                  <button
                    onClick={() => handleWatchEpisode(selectedEpisode)}
                    className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors group"
                  >
                    <div className="w-20 h-20 rounded-full bg-electric flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Play className="h-10 w-10 text-background fill-background ml-1" />
                    </div>
                  </button>
                )}
              </div>
              
              {/* Episode Info */}
              <div className="p-6">
                <div className="flex items-center space-x-3 mb-2">
                  <span className="px-2 py-1 bg-electric/20 text-electric text-sm rounded">
                    Season {selectedEpisode.season} • Episode {selectedEpisode.episode_number}
                  </span>
                  {selectedEpisode.duration && (
                    <span className="flex items-center space-x-1 text-sm text-muted-foreground">
                      <Clock className="h-4 w-4" />
                      <span>{selectedEpisode.duration}</span>
                    </span>
                  )}
                </div>
                
                <h3 className="text-2xl font-bold text-foreground mb-3">{selectedEpisode.title}</h3>
                
                <p className="text-foreground/80 leading-relaxed">
                  {selectedEpisode.description || 'No description available for this episode.'}
                </p>
                
                {/* Action Buttons */}
                <div className="flex items-center space-x-4 mt-6">
                  {(selectedEpisode.mux_playback_id || selectedEpisode.youtube_id) ? (
                    <button
                      onClick={() => handleWatchEpisode(selectedEpisode)}
                      className="flex items-center space-x-2 px-6 py-3 bg-electric text-background rounded-xl font-semibold hover:bg-electric/90 transition-colors"
                    >
                      <Play className="h-5 w-5 fill-background" />
                      <span>Play Episode</span>
                    </button>
                  ) : (
                    <div className="flex items-center space-x-2 px-6 py-3 bg-white/10 text-muted-foreground rounded-xl">
                      <Play className="h-5 w-5" />
                      <span>Coming Soon</span>
                    </div>
                  )}
                  <button
                    onClick={() => setSelectedEpisode(null)}
                    className="px-6 py-3 bg-white/10 text-foreground rounded-xl hover:bg-white/20 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Video Reviews Section */}
        <VideoReviewBubbles contentId={content.id} />

        {/* Comments Section */}
        <CommentsSection contentId={content.id} />
      </div>
    </div>
  );
};

export default ContentDetail;
